import { useCallback, useEffect, useState } from "react";
import { type ConnPerson, connectionsApi } from "../../lib/api/connections.js";
import { type DiscoverHit, discoveryApi } from "../../lib/api/discovery.js";
import { invitesApi } from "../../lib/api/invites.js";
import "./People.css";

type Tab = "people" | "requests" | "find";

function InviteWidget() {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    const r = await invitesApi.create();
    setBusy(false);
    if (r.success && r.data) setUrl(`${window.location.origin}${r.data.url}`);
  }
  async function copy() {
    if (url) await navigator.clipboard?.writeText(url);
  }

  return (
    <div className="ppl__invite">
      {url ? (
        <>
          <input readOnly value={url} className="ppl__search" aria-label="invite link" />
          <button type="button" className="ppl__btn ppl__btn--primary" onClick={copy}>
            Copy
          </button>
        </>
      ) : (
        <button
          type="button"
          className="ppl__btn ppl__btn--primary"
          onClick={create}
          disabled={busy}
        >
          {busy ? "Creating…" : "Invite a friend"}
        </button>
      )}
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  return (
    <span className="ppl__avatar" aria-hidden="true">
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

export function People() {
  const [tab, setTab] = useState<Tab>("people");

  return (
    <section className="ppl">
      <h1 className="ppl__h1">Your people</h1>
      <InviteWidget />
      <div className="ppl__tabs" role="tablist" aria-label="People sections">
        {(["people", "requests", "find"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={`ppl__tab${tab === t ? " is-active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t === "people" ? "My people" : t === "requests" ? "Requests" : "Find"}
          </button>
        ))}
      </div>
      {tab === "people" && <MyPeople />}
      {tab === "requests" && <Requests />}
      {tab === "find" && <Find />}
    </section>
  );
}

function MyPeople() {
  const [list, setList] = useState<ConnPerson[] | null>(null);
  useEffect(() => {
    connectionsApi.list().then((r) => setList(r.success && r.data ? r.data.connections : []));
  }, []);
  if (list === null) return <p className="ppl__muted">Loading…</p>;
  if (list.length === 0) {
    return <p className="ppl__muted">No connections yet — find your people, or share an invite.</p>;
  }
  return (
    <ul className="ppl__list">
      {list.map((p) => (
        <li key={p.id} className="ppl__row">
          <Avatar name={p.name} />
          <span className="ppl__name">
            {p.name}
            {p.handle && <span className="ppl__handle">@{p.handle}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Requests() {
  const [list, setList] = useState<ConnPerson[] | null>(null);
  const load = useCallback(() => {
    connectionsApi.requests().then((r) => setList(r.success && r.data ? r.data.requests : []));
  }, []);
  useEffect(() => load(), [load]);

  async function respond(id: string, action: "accept" | "decline") {
    await connectionsApi[action](id);
    load();
  }

  if (list === null) return <p className="ppl__muted">Loading…</p>;
  if (list.length === 0) return <p className="ppl__muted">No pending requests.</p>;
  return (
    <ul className="ppl__list">
      {list.map((p) => (
        <li key={p.id} className="ppl__row">
          <Avatar name={p.name} />
          <span className="ppl__name">
            {p.name}
            {p.handle && <span className="ppl__handle">@{p.handle}</span>}
          </span>
          <span className="ppl__actions">
            <button
              type="button"
              className="ppl__btn ppl__btn--primary"
              onClick={() => respond(p.id, "accept")}
            >
              Accept
            </button>
            <button type="button" className="ppl__btn" onClick={() => respond(p.id, "decline")}>
              Decline
            </button>
          </span>
        </li>
      ))}
    </ul>
  );
}

function Find() {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<DiscoverHit[]>([]);
  const [sent, setSent] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setHits([]);
      return;
    }
    const t = setTimeout(() => {
      discoveryApi.search(query).then((r) => setHits(r.success && r.data ? r.data.results : []));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  async function add(id: string) {
    setSent((s) => ({ ...s, [id]: true }));
    await connectionsApi.request(id);
  }

  return (
    <div>
      <input
        className="ppl__search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name or @handle"
        aria-label="search people"
        autoCapitalize="none"
      />
      <ul className="ppl__list">
        {hits.map((p) => {
          const requested = sent[p.id] || p.status === "pending";
          const connected = p.status === "accepted";
          return (
            <li key={p.id} className="ppl__row">
              <Avatar name={p.name} />
              <span className="ppl__name">
                {p.name}
                {p.handle && <span className="ppl__handle">@{p.handle}</span>}
              </span>
              <button
                type="button"
                className="ppl__btn ppl__btn--primary"
                disabled={requested || connected}
                onClick={() => add(p.id)}
              >
                {connected ? "Connected" : requested ? "Requested" : "Add"}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
